import type { CustomFieldDefinitionDocument } from '@/lib/db/rxdb/hooks/entities/custom-field-definitions'
import type { CustomFieldValueDocument } from '@/lib/db/rxdb/hooks/entities/custom-field-values'

export type CustomFieldType = 'string' | 'number' | 'date' | 'list'

export interface CustomFieldOption {
  id: string
  label: string
  color: string | null
  sortOrder: string
}

export interface StringFieldConfig {
  placeholder?: string
  maxLength?: number
}

export interface NumberFieldConfig {
  placeholder?: string
  min?: number
  max?: number
  decimalPlaces?: number
}

export interface DateFieldConfig {
  includeTime?: boolean
}

export type CustomFieldConfig = StringFieldConfig | NumberFieldConfig | DateFieldConfig | Record<string, never>

export type EditableCustomFieldConfig = Partial<
  StringFieldConfig & NumberFieldConfig & DateFieldConfig
>

export function parseConfig(config: string | null): EditableCustomFieldConfig {
  if (!config) return {}
  try {
    const parsed = JSON.parse(config)
    if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    return parsed as EditableCustomFieldConfig
  } catch {
    return {}
  }
}

export function parseOptions(options: string | null): Array<CustomFieldOption> {
  if (!options) return []
  try {
    const arr = JSON.parse(options)
    if (!Array.isArray(arr)) return []
    return [...arr].sort((a, b) =>
      String(a.sortOrder).localeCompare(String(b.sortOrder), undefined, { numeric: true }),
    )
  } catch {
    return []
  }
}

const ALLOWED_CONFIG_KEYS: Record<CustomFieldType, ReadonlyArray<keyof EditableCustomFieldConfig>> = {
  string: ['placeholder', 'maxLength'],
  number: ['placeholder', 'min', 'max', 'decimalPlaces'],
  date: ['includeTime'],
  list: [],
}

const NUMERIC_CONFIG_KEYS: ReadonlySet<keyof EditableCustomFieldConfig> = new Set([
  'maxLength',
  'min',
  'max',
  'decimalPlaces',
])
const STRING_CONFIG_KEYS: ReadonlySet<keyof EditableCustomFieldConfig> = new Set(['placeholder'])
const BOOLEAN_CONFIG_KEYS: ReadonlySet<keyof EditableCustomFieldConfig> = new Set(['includeTime'])

export function cleanConfig(
  fieldType: CustomFieldType,
  config: EditableCustomFieldConfig,
): EditableCustomFieldConfig {
  const allowed = ALLOWED_CONFIG_KEYS[fieldType]
  const out: EditableCustomFieldConfig = {}
  for (const key of allowed) {
    const value = config[key]
    if (value === undefined) continue
    if (NUMERIC_CONFIG_KEYS.has(key)) {
      if (typeof value === 'number' && Number.isFinite(value)) {
        ;(out as Record<string, unknown>)[key] = value
      }
    } else if (STRING_CONFIG_KEYS.has(key)) {
      if (typeof value === 'string' && value !== '') {
        ;(out as Record<string, unknown>)[key] = value
      }
    } else if (BOOLEAN_CONFIG_KEYS.has(key)) {
      if (typeof value === 'boolean') {
        ;(out as Record<string, unknown>)[key] = value
      }
    }
  }
  return out
}

export function isConfigEmpty(config: EditableCustomFieldConfig): boolean {
  return Object.keys(config).length === 0
}

/**
 * Extract the display value from a custom field value document.
 */
export function getFieldDisplayValue(
  fieldType: CustomFieldType,
  value: CustomFieldValueDocument | undefined,
  options: Array<CustomFieldOption>,
  locale?: string,
): string {
  if (!value) return ''

  switch (fieldType) {
    case 'string':
      return value.stringValue ?? ''
    case 'number':
      return value.numberValue != null ? String(value.numberValue) : ''
    case 'date':
      return value.dateValue != null
        ? new Date(value.dateValue).toLocaleDateString(locale)
        : ''
    case 'list': {
      const optionId = value.stringValue
      if (!optionId) return ''
      const option = options.find((o) => o.id === optionId)
      return option?.label ?? ''
    }
    default:
      return ''
  }
}

/**
 * Resolve the raw value for storage from user input.
 */
export function resolveFieldValue(
  fieldType: CustomFieldType,
  rawValue: string | number | null,
): Pick<CustomFieldValueDocument, 'stringValue' | 'numberValue' | 'dateValue'> {
  switch (fieldType) {
    case 'string':
    case 'list':
      return {
        stringValue: rawValue != null ? String(rawValue) : null,
        numberValue: null,
        dateValue: null,
      }
    case 'number':
      return {
        stringValue: null,
        numberValue: rawValue != null ? Number(rawValue) : null,
        dateValue: null,
      }
    case 'date':
      return {
        stringValue: null,
        numberValue: null,
        dateValue: rawValue != null ? Number(rawValue) : null,
      }
    default:
      return { stringValue: null, numberValue: null, dateValue: null }
  }
}

export type { CustomFieldDefinitionDocument, CustomFieldValueDocument }
